import { useEffect, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { BiSolidError } from "react-icons/bi";
import { FaPlus } from "react-icons/fa";
import { IoCloseSharp } from "react-icons/io5";
import "react-loading-skeleton/dist/skeleton.css";
import { useTranslation } from "react-i18next";
import { RiDeleteBin6Fill } from "react-icons/ri";
import { NavLink } from "react-router-dom";
import { toast } from "react-toastify";
import { useDebounce } from "use-debounce";
import { useSelector } from "react-redux";
import CancelIcon from "../../../assets/images/cancel-icon.svg";
import YesIcon from "../../../assets/images/Yes-Icon.svg";
import AdminPagination from "../../../component/pagination/AdminPagination";
import SkeletonLoading from "../../../component/SkeletonLoading";
import i18n from "../../../store/api/i18n";
import {
  useActivateMultiUserMutation,
  useDeleteMultiUserMutation,
  useDeleteSingleUserMutation,
  useGetAllUsersQuery,
} from "../../../store/api/userSlice";
import { getErrorMessage } from "../../../utils";
import "./vendors-schools.scss";
import { capitalizeString } from "../../../utils/dateAndTimeConversion";

const initialFilters = {
  role: "",
  email: "",
  active: "",
};

const initialPagination = {
  page: 1,
  limit: 10,
};

const VendorSchoolsPage = () => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [action, setAction] = useState("");
  const [deleteTargetEmail, setDeleteTargetEmail] = useState("");
  const [deleteReason, setDeleteReason] = useState("");

  const currentUser = useSelector((state) => state.UserReducer?.user);
  const isSuperAdmin = currentUser?.role === "super_admin";

  const [users, setusers] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 300);
  // state for single and multi user store id
  const [selectedUser, setSelectedUser] = useState({
    id: "",
    ids: [],
  });

  const [pagination, setPagination] = useState(initialPagination);

  const [filters, setFilters] = useState(initialFilters);

  const { data, isSuccess, isLoading, isError, error, isFetching, refetch } =
    useGetAllUsersQuery(
      {
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
      },
      { refetchOnMountOrArgChange: true },
    );

  // delete single user
  const [deleteSingleUser, { isLoading: isDeleteLoading }] =
    useDeleteSingleUserMutation();
  const [deleteMultiUser, { isLoading: isMultiDeleteLoading }] =
    useDeleteMultiUserMutation();

  const [activateMultiUser, { isLoading: isMultiActivateLoading }] =
    useActivateMultiUserMutation();

  if (isError) {
    toast.dismiss();
    toast.error(getErrorMessage(error));
  }

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleLimitChange = (limit) => {
    setPagination((prev) => ({
      ...prev,
      limit,
      page: 1,
    }));
  };

  useEffect(() => {
    if (isSuccess && data?.data) {
      setusers(data.data.users);
      setPagination(data?.data?.pagination);
    }
  }, [data, isSuccess]);

  const handleClose = () => {
    setShow(false);
    setDeleteReason("");
  };
  const handleModel = () => {
    if (action === "deleteMulti") {
      setShow(true);
    } else {
      handleUserAction();
    }
  };

  // for check user
  const handleCheckUser = (id) => {
    setSelectedUser((prev) => ({
      ...prev,
      ids: prev.ids.includes(id)
        ? prev.ids.filter((userId) => userId !== id) // remove
        : [...prev.ids, id], // add
    }));
  };

  // handle check all user
  const handleCheckAll = () => {
    const allIds = users.map((curElem) => curElem.id);
    setSelectedUser((prev) => ({
      ...prev,
      ids: prev.ids.length === users.length ? [] : allIds,
    }));
  };

  const handleUserAction = async () => {
    toast.dismiss();
    if (!action) return;

    if (selectedUser.ids.length === 0) {
      toast.error(t("Please select at least one user"));
      // toast.error(t("select_at_least_one_user"));
      return;
    }

    try {
      let response;
      action === "singleDelete"
        ? (response = await deleteSingleUser({
            id: selectedUser.id,
            reason: deleteReason,
          }).unwrap())
        : action === "deleteMulti"
          ? (response = await deleteMultiUser({
              userIds: selectedUser.ids,
            }).unwrap())
          : (response = await activateMultiUser({
              userIds: selectedUser.ids,
              active: action === "activeMlti",
            }).unwrap());

      if (response.statusCode === 201 || response.statusCode === 200) {
        toast.success(response.message);
        setShow(false);
        setSelectedUser({ id: "", ids: [] });
        setAction("");
        setDeleteReason("");
        refetch();
      }
    } catch (err) {
      const mesg = err.data?.message || "Something went wrong";
      toast.error(mesg);
    }
  };

  const handleResetFilters = () => {
    setPagination((prev) => ({ ...prev, ...initialPagination }));
    setFilters(initialFilters);
    setAction("");
    setSearch("");
  };

  // search State & Debounce Setup
  useEffect(() => {
    if (debouncedSearch) {
      setPagination((prev) => ({ limit: 10, page: 1 }));
      setFilters((prev) => ({ ...prev, email: search }));
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (!search.trim()) {
      setFilters((prev) => ({ ...prev, email: "" }));
    }
  }, [search]);

  // Language Change Handler
  useEffect(() => {
    const h = () => {
      refetch?.();
    };
    i18n.on("languageChanged", h);
    return () => i18n.off("languageChanged", h);
  }, [refetch]);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap w-100 mb-2">
        <div className="total">
          {t("total")} {t("users")}: {pagination?.total}
        </div>

        <div className="d-flex justify-content-between align-items-center gap-2">
          <select
            className="form-select"
            value={action}
            onChange={(e) => setAction(e.target.value)}
          >
            <option value="">
              {t("select")} {t("bulk")} {t("actions")}
            </option>
            <option value="activeMlti">
              {t("activate")} {t("selected")} {t("users")}
            </option>
            <option value="inactiveMlti">
              {t("inactivate")} {t("selected")} {t("users")}
            </option>
          </select>
          <button
            className="btn btn-primary me-2"
            onClick={handleModel}
            disabled={isMultiActivateLoading}
          >
            {isMultiActivateLoading ? (
              <div className="spinner-border loaderStyle2" role="status">
                <span className="sr-only p-0"></span>
              </div>
            ) : (
              t("go")
            )}
          </button>
          <span className="selected-info">
            {selectedUser.ids.length} of {users.length} {t("selected")}
          </span>
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap w-100 mb-2">
        <div className="d-flex flex-wrap gap-2 ms-auto">
          <div className="d-flex flex-grow-1">
            <input
              type="text"
              className="form-control custom-field-one"
              placeholder={`${t("search")}..`}
              value={search}
              onChange={(e) => setSearch(e.target.value.trim())}
            />
          </div>
          <div className="d-flex flex-grow-1">
            <select
              className="form-select"
              value={filters.role}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, role: e.target.value }))
              }
            >
              <option value="">
                {t("select")} {t("user")} {t("role")}
              </option>
              <option value="school">{t("userRole.school")}</option>
              <option value="vendor">{t("userRole.vendor")}</option>
              <option value="driver">{t("userRole.driver")}</option>
              <option value="parent">{t("userRole.parent")}</option>
            </select>
          </div>
          <div className="d-flex flex-grow-1">
            <select
              className="form-select"
              value={filters.active}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, active: e.target.value }))
              }
            >
              <option value="">
                {t("select")} {t("user")} {t("status")}
              </option>
              <option value="yes">{t("yes")}</option>
              <option value="no">{t("no")}</option>
            </select>
          </div>
          <div className="d-flex gap-2 ms-auto">
            <button
              className="btn btn-primary addDriver "
              onClick={handleResetFilters}
            >
              {t("btns.reset")}
            </button>

            <NavLink
              to="add"
              className="page-add-btn d-flex align-items-center gap-2"
            >
              <FaPlus />{" "}
              {`${t("add")} ${t("vendors")}/${t("school")} ${t("owner")}`}
            </NavLink>
          </div>
        </div>
      </div>

      <div className="adminVendorSchools">
        <div className="mb-2 filterBox justify-content-start align-items-start">
          <li></li>
        </div>

        <div className="table m-0">
          <table>
            <thead>
              <tr>
                <th>
                  <Form.Check
                    type="checkbox"
                    // checked={selectedUser.ids.includes(curElem.id)}
                    onChange={handleCheckAll}
                  />
                </th>
                <th>
                  {t("email")} {t("address")}
                </th>
                <th>
                  {t("user")} {t("type")}
                </th>
                <th>{t("activate")}</th>
                <th>
                  {t("contact")} {t("number")}
                </th>
                <th>{t("name")}</th>
                {isSuperAdmin && <th>{t("btns.delete")}</th>}
              </tr>
            </thead>
            <tbody>
              {isLoading || isFetching ? (
                <SkeletonLoading length={7} />
              ) : users.length > 0 ? (
                users.map((curElem, i) => (
                  <tr key={i}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={selectedUser.ids.includes(curElem.id)}
                        onChange={() => handleCheckUser(curElem.id)}
                      />
                    </td>
                    <td>
                      {/* <Link
                            to={`/user-management/edit/${curElem.id}`}
                            className="ps-4 fw-bold"
                            style={{ color: "#007bff" }}
                          > */}
                      {curElem.email ? curElem.email : "N/A"}
                      {/* </Link> */}
                    </td>
                    <td className="text-nowrap">
                      {curElem.role ? capitalizeString(curElem.role) : "N/A"}
                    </td>
                    <td>
                      <img
                        src={
                          curElem?.status === "active" ? YesIcon : CancelIcon
                        }
                        alt={curElem?.status === "active" ? "True" : "false"}
                        className="status-icon"
                      />
                    </td>
                    <td>{curElem.phone ? curElem.phone : "N/A"}</td>
                    <td>{curElem.full_name ? curElem.full_name : "N/A"}</td>
                    {isSuperAdmin && (
                      <td>
                        {curElem?.role !== "super_admin" && (
                          <Button
                            className="btn-danger p-2"
                            onClick={() => {
                              setShow(true);
                              setAction("singleDelete");
                              setDeleteTargetEmail(
                                curElem.email ||
                                  curElem.full_name ||
                                  "this user",
                              );
                              setSelectedUser((prev) => ({
                                ...prev,
                                id: curElem.id,
                              }));
                            }}
                          >
                            <RiDeleteBin6Fill />
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr className="text-center">
                  <td colSpan="12">
                    <div>
                      <span>
                        {" "}
                        {t("no")} {t("users")} {t("found")}
                      </span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <AdminPagination
        totalPages={pagination.pages}
        currentPage={pagination.page}
        limit={pagination.limit}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
      />

      <Modal show={show} centered>
        <Button className="closePopup" onClick={() => handleClose("show")}>
          <IoCloseSharp />
        </Button>
        <div className="popupBox p-3 p-md-5">
          <div>
            <div className="text-center mb-3 errorIcon">
              <BiSolidError />
            </div>
            <div className="text-center mb-2 deleteHd">
              {t("confirmDelete")}
            </div>
            {action === "singleDelete" && deleteTargetEmail && (
              <div className="text-center mb-2" style={{ fontSize: "0.9rem" }}>
                <strong>{deleteTargetEmail}</strong>
              </div>
            )}
            {action === "deleteMulti" && selectedUser.ids.length > 0 && (
              <div className="text-center mb-2" style={{ fontSize: "0.9rem" }}>
                {selectedUser.ids.length} {t("users")}
              </div>
            )}
            <div
              className="mb-3 p-2 text-center"
              style={{
                background: "#fff3cd",
                border: "1px solid #ffc107",
                borderRadius: "8px",
                fontSize: "0.82rem",
                color: "#856404",
              }}
            >
              ⚠️ This action <strong>cannot be undone</strong>. The user will be
              permanently removed and will no longer be able to log in.
            </div>
            {action === "singleDelete" && (
              <div className="mb-3">
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Reason for deletion (optional)..."
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  style={{ fontSize: "0.9rem", resize: "none" }}
                />
              </div>
            )}
            <div className="text-center">
              <Button
                className="smallBtn cancel me-3"
                onClick={() => handleClose("show")}
                disabled={isMultiDeleteLoading || isDeleteLoading}
              >
                {t("btns.cancel")}
              </Button>
              <Button
                className="smallBtn auto"
                onClick={handleUserAction}
                disabled={isMultiDeleteLoading || isDeleteLoading}
              >
                {isMultiDeleteLoading || isDeleteLoading ? (
                  <div className="spinner-border" role="status">
                    <span className="sr-only p-0"></span>
                  </div>
                ) : (
                  t("btns.delete")
                )}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default VendorSchoolsPage;
